import { useAtom } from '@reatom/npm-react'
import { memo } from 'react'

import { usePairDevTokensInfo } from '@root/entities/pair/hooks/use-pair-dev-tokens-info'
import { CountMax3DigitsFormatted } from '@root/shared/core/format'
import {
  CrownOutlinedIcon,
  TooltipCard,
  TooltipCardParentGuardProvider,
} from '@root/shared/ui-kit'

import { ExploreDevTokensTooltip } from '../../explore-dev-tooltip/explore-dev-tokens-tooltip'
import { PairExploreItemAuditItem as Item } from '../pair-explore-item-audit-item'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
  size?: number
}

export const PairExploreItemAuditDevTokens = memo(
  ({ size = 12, pair }: Props) => {
    const [creatorStats] = useAtom(pair.creatorStats)

    const devTokensRenderData = usePairDevTokensInfo(pair)

    const hasNoDevStats = !devTokensRenderData || !creatorStats
    const hasEmptyDevStats =
      devTokensRenderData?.totalPoolsWithoutCurrent === 0 &&
      devTokensRenderData.bondedTokensWithoutCurrent === 0

    if (hasNoDevStats || hasEmptyDevStats) {
      return (
        <Item pair={pair}>
          <CrownOutlinedIcon size={size} />
          <span>0/0</span>
        </Item>
      )
    }

    return (
      <TooltipCardParentGuardProvider withCascadePreviews>
        <TooltipCard
          sideOffset={5}
          closeArea={
            <ExploreDevTokensTooltip.CloseArea
              devTokensInfo={devTokensRenderData}
            />
          }
          clickArea={
            devTokensRenderData.topTokensWithoutCurrent.length ===
            0 ? undefined : (
              <ExploreDevTokensTooltip.ClickArea
                devTokensInfo={devTokensRenderData}
              />
            )
          }
        >
          <Item
            pair={pair}
            intent={creatorStats.isDangerous ? 'Danger' : 'Warning'}
          >
            <CrownOutlinedIcon size={size} />
            <span>
              {devTokensRenderData.bondedTokensWithoutCurrent}/
              <CountMax3DigitsFormatted
                count={devTokensRenderData.totalPoolsWithoutCurrent}
              />
            </span>
          </Item>
        </TooltipCard>
      </TooltipCardParentGuardProvider>
    )
  },
)
